/** DECUPAREA HÂRTIEI DE CALC: cum se uită asistentul de-aproape la plan.
 *
 *  DE CE NU AJUNGE POZA DIN MESAJ. Imaginea trimisă cu mesajul omului trece
 *  prin API micșorată pe la 1568 px pe latura lungă. Un plan A3 scanat are
 *  cotele scrise cu 2,5 mm — la 1568 px pe 42 de cm, o cifră de cotă are patru
 *  pixeli înălțime și e o pată. Ochiul omului rezolvă asta apropiindu-se de
 *  hârtie; modelul n-are cum, decât dacă i se dă chiar bucata, mărită.
 *
 *  DECI CALCUL SE ȚINE MAI MARE (2400 px, v. `viewer/tronson/calc.ts`) și de
 *  aici se taie zone din el, fiecare recompusă la o lățime la care se citește.
 *  Un sfert de plan mărit de două ori costă cât o captură — și e diferența
 *  dintre «scrie ceva pe cotă» și «scrie 4,20».
 *
 *  PNG, nu JPEG: o cifră de cotă mărită dintr-o poză deja comprimată o dată nu
 *  mai are nevoie de încă un rând de artefacte exact pe muchiile literelor.
 */

/** Cât poate avea, pe latura lungă, bucata întoarsă modelului. Peste atât nu
 *  se mai citește nimic în plus, iar fiecare pixel e context plătit. */
const LATURA = 1400;

export type Decupaj = {
  /** `data:image/png;base64,…`. */
  url: string;
  /** Zona tăiată, în pixelii imaginii — modelul citește coordonate de aici. */
  zona: [number, number, number, number];
  /** De câte ori s-a mărit față de pixelii ei. */
  marire: number;
};

/** Taie din imaginea dată zona cerută (pixelii ei) și o mărește cât încape.
 *
 *  Fără zonă, întreaga imagine. Zona se strânge la marginile imaginii, ca o
 *  cerere cu 200 de pixeli pe lângă colț să întoarcă colțul, nu o eroare: cine
 *  cere o zonă o cere din ochi, de pe o privire de dinainte.
 */
export function decupeaza(
  url: string, latime: number, inaltime: number,
  zona?: [number, number, number, number],
): Promise<Decupaj> {
  return new Promise((da, nu) => {
    const poza = new Image();
    poza.onload = () => {
      const x0 = Math.max(0, Math.min(latime - 1, Math.round(zona?.[0] ?? 0)));
      const y0 = Math.max(0, Math.min(inaltime - 1, Math.round(zona?.[1] ?? 0)));
      const x1 = Math.max(x0 + 1, Math.min(latime, Math.round(zona?.[2] ?? latime)));
      const y1 = Math.max(y0 + 1, Math.min(inaltime, Math.round(zona?.[3] ?? inaltime)));
      const w = x1 - x0;
      const h = y1 - y0;
      const k = Math.min(LATURA / Math.max(w, h), 4);
      const panza = document.createElement("canvas");
      panza.width = Math.max(1, Math.round(w * k));
      panza.height = Math.max(1, Math.round(h * k));
      const c = panza.getContext("2d");
      if (!c) { nu(new Error("fără pânză")); return; }
      // Fond alb: un plan scanat are fundal alb oricum, iar transparența ar
      // ieși neagră în vizualizatorul modelului.
      c.fillStyle = "#ffffff";
      c.fillRect(0, 0, panza.width, panza.height);
      c.imageSmoothingQuality = "high";
      c.drawImage(poza, x0, y0, w, h, 0, 0, panza.width, panza.height);
      try {
        da({ url: panza.toDataURL("image/png"), zona: [x0, y0, x1, y1],
             marire: Math.round(k * 100) / 100 });
      } catch (e) { nu(e as Error); }
    };
    poza.onerror = () => nu(new Error("imaginea nu s-a încărcat"));
    poza.src = url;
  });
}
